import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { RefreshCw, CheckCircle, XCircle, AlertCircle, PlayCircle } from 'lucide-react';

const TallyDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchQueue();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/tally/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQueue = async () => {
    try {
      const response = await api.get('/tally/queue?limit=50');
      setQueue(response.data);
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    }
  };

  const processQueue = async () => {
    setProcessing(true);
    try {
      const response = await api.post('/tally/queue/process');
      alert(`Processed: ${response.data.processed}, Failed: ${response.data.failed}`);
      fetchDashboard();
      fetchQueue();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to process queue');
    } finally {
      setProcessing(false);
    }
  };

  const retryItem = async (queueId) => {
    try {
      await api.post(`/tally/queue/${queueId}/retry`);
      fetchQueue();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to retry');
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      SALES_INVOICE: 'Sales Invoice',
      PURCHASE_INVOICE: 'Purchase Invoice',
      DEBIT_NOTE: 'Debit Note',
      STOCK_QUERY: 'Stock Query',
      OUTSTANDING_SYNC: 'Outstanding'
    };
    return labels[type] || type;
  };

  const getStatusIcon = (status) => {
    if (status === 'success') return <CheckCircle size={16} color="#34D399" />;
    if (status === 'failed') return <XCircle size={16} color="#F87171" />;
    return <AlertCircle size={16} color="#FBBF24" />;
  };

  if (loading) {
    return <div data-testid="loading-indicator">Loading...</div>;
  }

  return (
    <div data-testid="tally-dashboard-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Tally Integration</h1>
        <button
          onClick={processQueue}
          className="btn btn-primary"
          disabled={processing}
          data-testid="process-queue-btn"
        >
          <PlayCircle size={16} />
          {processing ? 'Processing...' : 'Process Queue'}
        </button>
      </div>

      {/* Configuration Status */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h2 className="card-title">Configuration Status</h2>
        </div>
        <div className="grid grid-cols-4">
          <div>
            <div className="text-sm text-muted">Company</div>
            <div className="font-semibold">{dashboard?.config?.company || 'Not Configured'}</div>
          </div>
          <div>
            <div className="text-sm text-muted">Auto Sync</div>
            <div className="font-semibold">
              {dashboard?.config?.auto_sync ? (
                <span className="badge badge-success">Enabled</span>
              ) : (
                <span className="badge badge-warning">Disabled</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted">Last Sync</div>
            <div className="font-semibold text-sm">
              {dashboard?.config?.last_sync
                ? new Date(dashboard.config.last_sync).toLocaleString()
                : 'Never'}
            </div>
          </div>
          <div>
            <a href="/tally/config" className="btn btn-secondary btn-sm">
              Configure
            </a>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4" style={{ marginBottom: '24px' }}>
        <div className="stat-card" data-testid="stat-total-queue">
          <div className="stat-value">{dashboard?.queue?.total || 0}</div>
          <div className="stat-label">Total Queued</div>
        </div>
        <div className="stat-card" data-testid="stat-pending">
          <div className="stat-value" style={{ color: '#FBBF24' }}>{dashboard?.queue?.pending || 0}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card" data-testid="stat-success">
          <div className="stat-value" style={{ color: '#34D399' }}>{dashboard?.queue?.success || 0}</div>
          <div className="stat-label">Synced</div>
        </div>
        <div className="stat-card" data-testid="stat-failed">
          <div className="stat-value" style={{ color: '#F87171' }}>{dashboard?.queue?.failed || 0}</div>
          <div className="stat-label">Failed</div>
        </div>
      </div>

      {/* Queue Items */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h2 className="card-title">Sync Queue</h2>
            <button onClick={fetchQueue} className="btn btn-ghost btn-sm" data-testid="refresh-queue-btn">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
        {queue.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            No items in queue
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Reference</th>
                  <th>Status</th>
                  <th>Retry Count</th>
                  <th>Created</th>
                  <th>Error</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((item) => (
                  <tr key={item.id} data-testid={`queue-row-${item.id}`}>
                    <td>{getTypeLabel(item.type)}</td>
                    <td className="font-mono text-sm">
                      {item.payload?.order_number || item.payload?.po_number || item.payload?.debit_note_number || '-'}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(item.status)}
                        <span className="text-sm">{item.status}</span>
                      </div>
                    </td>
                    <td className="tabular-nums">{item.retry_count} / {item.max_retries}</td>
                    <td className="text-sm">{new Date(item.created_at).toLocaleString()}</td>
                    <td className="text-xs" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.error_log || '-'}
                    </td>
                    <td>
                      {item.status === 'failed' && item.retry_count < item.max_retries && (
                        <button
                          onClick={() => retryItem(item.id)}
                          className="btn btn-ghost btn-sm"
                          data-testid={`retry-btn-${item.id}`}
                        >
                          <RefreshCw size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TallyDashboard;
